import {
  FACEBOOK,
  GOOGLE,
  INSTAGRAM,
  LINKEDIN_BUSINESS_PAGE,
  LINKEDIN_PERSONAL_ACCOUNT,
  PINTEREST,
  PLATFORM_LIST_NAMES,
  SELECTED_PLATFORM_COLOR_CODE,
  TIKTOK_BUSINESS_PAGE,
  TIKTOK_PERSONAL_ACCOUNT,
  TWITTER,
  WHATSAPP,
  YOUTUBE
} from '@/config/constants';
import { BarChartData3 } from '@/lomavisSharedUi/atoms/charts/LomavisBarChart3';
import { MetricName } from '@/lomavisSharedUi/molecules/charts/ILomavisPlatformContentType1';
import { isPlatformEnabled } from '@/redux/slices/lomavisCreator/lomavisCreator';
import { formatDateAsISODateString } from '@/utils/dateUtils';

export const MULTI_IMAGE = 'multi_image_post';
export const REEL = 'reel_post';
export const SINGLE_IMAGE = 'single_image_post';
export const STORY = 'story_post';
export const TEXT = 'text_post';
export const VIDEO = 'video_post';

export const DATA_PAGINATION_COUNT = 20;

export const METRIC_NAMES = {
  IMPRESSIONS: 'impressions',
  REACH: 'reach',
  REACTIONS: 'reactions',
  SHARES: 'shares',
  COMMENTS: 'comments',
  INTERACTIONS: 'interactions',
  ENGAGEMENT: 'engagement',
  POST_COUNT: 'postCount'
};

export const allContentTypePerformanceConfig = [
  {
    type: STORY,
    metricName: `analytics_postPerformance.${STORY}`,
    name: 'Story',
    color: '#010101'
  },
  {
    type: REEL,
    metricName: `analytics_postPerformance.${REEL}`,
    name: 'Reel',
    color: '#e1306c'
  },
  {
    type: MULTI_IMAGE,
    metricName: `analytics_postPerformance.${MULTI_IMAGE}`,
    name: 'Multi image',
    color: '#1877F2'
  },

  {
    type: SINGLE_IMAGE,
    metricName: `analytics_postPerformance.${SINGLE_IMAGE}`,
    name: 'Single image',
    color: '#0077b5'
  },

  {
    type: VIDEO,
    metricName: `analytics_postPerformance.${VIDEO}`,
    name: 'Video',
    color: '#1da1f2'
  },
  {
    type: TEXT,
    metricName: `analytics_postPerformance.${TEXT}`,
    name: 'Text',
    color: '#ff0000'
  }
];

export const allPlatformsPostPerformanceConfig = [
  {
    plotName: 'overview',
    metricName: 'impressions',

    title: 'analytics_postPerformance.impressions',
    helpText:
      'analytics_postPerformance.total_number_of_impressions_across_platforms',
    cssIcon: 'fa-duotone fa-solid fa-users',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  },
  {
    plotName: 'overview',
    metricName: 'reach',

    title: 'analytics_postPerformance.reach',
    helpText:
      'analytics_postPerformance.total_number_of_reach_across_platforms',
    cssIcon: 'fa-duotone fa-solid fa-heart',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  },
  {
    plotName: 'overview',
    metricName: 'reactions',

    title: 'analytics_postPerformance.reactions',
    helpText:
      'analytics_postPerformance.total_number_of_reactions_across_platforms',
    cssIcon: 'fa-duotone fa-solid fa-comment',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  },
  {
    plotName: 'overview',
    metricName: 'comments',

    title: 'analytics_postPerformance.comments',
    helpText:
      'analytics_postPerformance.total_number_of_comments_across_platforms',
    cssIcon: 'fa-duotone fa-solid fa-comments  ',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  },
  {
    plotName: 'overview',
    metricName: 'shares',

    title: 'analytics_postPerformance.shares',
    helpText:
      'analytics_postPerformance.total_number_of_shares_across_platforms',
    cssIcon: 'fa-duotone fa-solid fa-share',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  },
  {
    plotName: 'overview',
    metricName: 'interactions',

    title: 'analytics_postPerformance.interactions',
    helpText:
      'analytics_postPerformance.total_number_of_interactions_across_platforms',
    cssIcon: 'fa-duotone fa-solid fa-at',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  },

  {
    plotName: 'overview',
    metricName: 'engagement',

    title: 'analytics_postPerformance.engagement',
    helpText:
      'analytics_postPerformance.total_number_of_engagement_across_platforms',
    cssIcon: 'fa-duotone fa-solid fa-id-badge',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  },

  {
    plotName: 'overview',
    metricName: 'postCount',

    title: 'analytics_postPerformance.total_post_count',
    helpText:
      'analytics_postPerformance.total_number_of_posts_across_platforms',
    cssIcon: 'fa-duotone fa-copy',
    cssIconColor: '#7239EA',
    showGrowthBadge: false
  }
];

export function getAbsoluteKPIs(array: any[], keyPath: string): number {
  if (!Array.isArray(array)) return 0;

  if (keyPath === 'sum_postCount') {
    return getAbsolutePostCount(array);
  }
  function getValueByPath(obj: any, path: string): any {
    return path
      .split(/[\.\[\]]+/)
      .filter(Boolean)
      .reduce((o, p) => (o && typeof o === 'object' ? o[p] : undefined), obj);
  }

  const sum = array.reduce((total, item) => {
    const val = getValueByPath(item, keyPath);
    const num =
      typeof val === 'number'
        ? val
        : typeof val === 'string'
        ? parseFloat(val) || 0
        : 0;
    return total + num;
  }, 0);

  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

export const getAverageKPIs = (
  array: any[],
  key: string,
  length: number,
  dayCount: number
) => {
  const total = getAbsoluteKPIs(array, key);
  let average;
  if (key === 'sum_postCount') {
    average = total / dayCount;
  } else {
    average = total / length;
  }

  return parseFloat(average.toFixed(2));
};

export const getPostPerformanceMetrics = (
  data: any,
  oldData: any,
  config: any[],
  t: any
) => {
  const entries = config.map((entry) => {
    const analyticsArray = data.map((item) => item?.analytics);
    const oldAnalyticsArray = oldData.map((item) => item?.analytics);

    const current = getAbsoluteKPIs(analyticsArray, `sum_${entry?.metricName}`);

    const previous = getAbsoluteKPIs(
      oldAnalyticsArray,
      `sum_${entry?.metricName}`
    );
    const individual = analyticsArray?.map((item) => item?.sum_engagement);

    return {
      title: t(entry?.title),
      helpText: t(entry?.helpText),
      cssIcon: entry?.cssIcon,
      cssIconColor: entry?.cssIconColor,
      current: current,
      previous: previous,
      metricName: entry?.metricName,
      showGrowthBadge: entry?.showGrowthBadge || false
    };
  });

  // 🔑 post-process engagement
  const impressionEntry = entries.find(
    (e) => e.metricName === METRIC_NAMES.IMPRESSIONS
  );
  const engagementEntry = entries.find(
    (e) => e.metricName === METRIC_NAMES.ENGAGEMENT
  );
  const interactionEntry = entries.find(
    (e) => e.metricName === METRIC_NAMES.INTERACTIONS
  );
  const reachEntry = entries.find((e) => e.metricName === METRIC_NAMES.REACH);

  if (engagementEntry && interactionEntry && reachEntry) {
    engagementEntry.current = reachEntry.current
      ? parseFloat(
          ((interactionEntry.current / reachEntry.current) * 100).toFixed(2)
        )
      : 0;
    engagementEntry.previous = reachEntry.previous
      ? (interactionEntry.previous / reachEntry.previous) * 100
      : 0;
    engagementEntry['dataUnit'] = '%';
  }

  if (reachEntry === null || 0) {
    reachEntry.current = impressionEntry.current;
    reachEntry.previous = impressionEntry.previous;
  }

  return entries.filter((entry) => entry !== null);
};

export const getAveragePostPerformanceMetrics = (
  data: any,
  oldData: any,
  config: any[],
  t: any,
  dayCount: number
) => {
  const entries = config.map((entry) => {
    const analyticsArray = data.map((item) => item?.analytics);
    const oldAnalyticsArray = oldData.map((item) => item?.analytics);

    const totalPosts = getAbsolutePostCount(analyticsArray);

    const current = getAverageKPIs(
      analyticsArray,
      `sum_${entry?.metricName}`,
      totalPosts,
      dayCount
    );
    const previous = getAverageKPIs(
      oldAnalyticsArray,
      `sum_${entry?.metricName}`,
      totalPosts,
      dayCount
    );

    return {
      title: t(entry?.title),
      helpText: t(entry?.helpText),
      cssIcon: entry?.cssIcon,
      cssIconColor: entry?.cssIconColor,
      current: current,
      previous: previous,
      metricName: entry?.metricName,
      showGrowthBadge: entry?.showGrowthBadge || false
    };
  });

  const engagementEntry = entries.find(
    (e) => e.metricName === METRIC_NAMES.ENGAGEMENT
  );
  engagementEntry['dataUnit'] = '%';

  return entries.filter((entry) => entry !== null);
};

export const getContentTypeMetrics = (
  data: any[],
  config: { type: string; name: string; color: string }[],
  t?: any
) => {
  // Calculate raw counts
  const rawEntries = config.map((entry) => {
    const count = data.reduce((acc, item) => {
      return acc + (item[`content_type_is_${entry.type}`] === true ? 1 : 0);
    }, 0);

    return {
      value: count,
      name: entry.name,
      color: entry.color
    };
  });

  // Total count
  const total = rawEntries.reduce((acc, entry) => acc + entry.value, 0);

  // Convert to percentages (2 decimal places)
  const entries = rawEntries.map((entry) => ({
    ...entry,
    value: total > 0 ? parseFloat(((entry.value / total) * 100).toFixed(2)) : 0
  }));

  // Keep only non-zero values
  return entries.filter((entry) => entry.value > 0);
};

export const getPlatformMetrics = (data: any, config: any[], t?: any) => {
  // first, calculate the sum across all platforms
  const total = config.reduce((acc, entry) => {
    return (
      acc +
      data.reduce((sum, item) => {
        return sum + (item[`${entry}_post`] === true ? 1 : 0);
      }, 0)
    );
  }, 0);

  const entries = config.map((entry) => {
    const SUM = data.reduce((acc, item) => {
      return acc + (item[`${entry}_post`] === true ? 1 : 0);
    }, 0);

    const percentage = total > 0 ? (SUM / total) * 100 : 0;

    return {
      value: Number(percentage.toFixed(2)), // ✅ 2 decimal places
      name: PLATFORM_LIST_NAMES[entry],
      color: SELECTED_PLATFORM_COLOR_CODE[entry]
    };
  });

  return entries.filter((metric) => metric.value > 0);
};
export function filterExistingPlatforms(
  platforms: string[],
  data: any[]
): string[] {
  return platforms.filter((platform) =>
    data.some((item) => Boolean(item[`${platform}_post`]))
  );
}

function getAbsolutePostCount(array: any[]): number {
  if (!Array.isArray(array)) return 0;

  return array.reduce((acc, item) => {
    const overview = item?.data_as_json?.overview as
      | Record<string, boolean>
      | undefined;
    if (!overview) return acc;

    const countForItem = Object.values(overview).reduce((sum, val) => {
      return sum + (val ? 1 : 0);
    }, 0);

    return acc + countForItem;
  }, 0);
}

function getPostCount(array: any[], platform: string): number {
  if (!Array.isArray(array)) return 0;

  return array.reduce((acc, item) => {
    const overview = item?.analytics?.data_as_json?.overview;
    if (!overview) return acc;

    // Check if overview has the given platform
    return acc + (overview[platform] ? 1 : 0);
  }, 0);
}

export const getPlatformPerformanceMetrics = (
  data: any,
  config: any[],
  metric: string,
  t: any
) => {
  const analyticsArray = data.map((item) => item?.analytics);

  const entries = config.map((entry) => {
    if (metric === METRIC_NAMES.ENGAGEMENT) {
      // ✅ Engagement = (interactions / reach) * 100 per platform
      const interactions = getAbsoluteKPIs(
        analyticsArray,
        `data_as_json.overview[${entry}].${METRIC_NAMES.INTERACTIONS}`
      );
      const reach = getAbsoluteKPIs(
        analyticsArray,
        `data_as_json.overview[${entry}].${METRIC_NAMES.REACH}`
      );

      const engagement =
        reach > 0 ? parseFloat(((interactions / reach) * 100).toFixed(2)) : 0;

      return {
        value: engagement,
        name: PLATFORM_LIST_NAMES[entry],
        color: SELECTED_PLATFORM_COLOR_CODE[entry]
      };
    }

    const SUM =
      metric === 'postCount'
        ? getPostCount(data, entry)
        : getAbsoluteKPIs(
            analyticsArray,
            `data_as_json.overview[${entry}].${metric}`
          );

    return {
      value: SUM ?? 0,
      name: PLATFORM_LIST_NAMES[entry],
      color: SELECTED_PLATFORM_COLOR_CODE[entry]
    };
  });

  return entries.filter((metric) => metric.value !== null);
};

export const getPerformanceSumMetrics = (
  data: any,
  config: string[], // array of platform keys, e.g. ['facebook','instagram']
  metric: string,
  t: any
): number => {
  const analyticsArray = data.map((item) => item?.analytics);

  // ✅ Engagement = (Σ interactions over platforms) / (Σ reach over platforms) * 100
  if (metric === METRIC_NAMES.ENGAGEMENT) {
    const totals = config.reduce(
      (acc, platform) => {
        const interactions =
          getAbsoluteKPIs(
            analyticsArray,
            `data_as_json.overview[${platform}].${METRIC_NAMES.INTERACTIONS}`
          ) || 0;

        const reach =
          getAbsoluteKPIs(
            analyticsArray,
            `data_as_json.overview[${platform}].${METRIC_NAMES.REACH}`
          ) || 0;

        acc.interactions += interactions;
        acc.reach += reach;
        return acc;
      },
      { interactions: 0, reach: 0 }
    );

    return totals.reach > 0
      ? Math.round(
          ((totals.interactions / totals.reach) * 100 + Number.EPSILON) * 100
        ) / 100
      : 0;
  }

  // ✅ Post count is still special (per-platform existence in overview)
  if (metric === METRIC_NAMES.POST_COUNT) {
    return config.reduce(
      (acc, platform) => acc + (getPostCount(data, platform) || 0),
      0
    );
  }

  // ✅ Regular metrics: sum across platforms listed in config
  const sum = config.reduce((acc, platform) => {
    const val =
      getAbsoluteKPIs(
        analyticsArray,
        `data_as_json.overview[${platform}].${metric}`
      ) || 0;
    return acc + val;
  }, 0);

  return Math.round((sum + Number.EPSILON) * 100) / 100;
};

// export function getDateContentMetrics(
//   data: any[],
//   config: Array<{ type: string; name: string }>
// ): BarChartData[] {
//   const map = new Map<string, Record<string, number>>();

//   for (const item of data) {
//     const dateKey = toDateOnlyIso(item.publication_date);

//     // Initialize counters for this date if not already present
//     if (!map.has(dateKey)) {
//       map.set(dateKey, Object.fromEntries(config.map((c) => [c.name, 0])));
//     }

//     const counts = map.get(dateKey)!;
//     for (const { type, name } of config) {
//       if (item[`content_type_is_${type}`] === true) {
//         counts[name]++;
//       }
//     }
//   }

//   // Convert and sort by date descending
//   return Array.from(map, ([label, data]) => ({ label, data })).sort((a, b) =>
//     a.label.localeCompare(b.label)
//   );
// }

export function getDateContentMetrics(
  data: any[],
  config: Array<{ type: string; name: string }>
): BarChartData3[] {
  // Step 1: Group data by date, counting content types
  const byDate = new Map<string, Record<string, number>>();
  for (const item of data) {
    const date = formatDateAsISODateString(item.publication_date);
    if (!byDate.has(date)) {
      byDate.set(date, Object.fromEntries(config.map((c) => [c.name, 0])));
    }
    const counts = byDate.get(date)!;
    for (const { type, name } of config) {
      if (item[`content_type_is_${type}`] === true) {
        counts[name]++;
      }
    }
  }

  // Step 2: Compute full continuous date range
  const dateStrings = Array.from(byDate.keys()).sort();
  const start = new Date(dateStrings[0]);
  const end = new Date(dateStrings[dateStrings.length - 1]);

  const allDates: string[] = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    allDates.push(d.toISOString().slice(0, 10));
  }

  // Step 3: Build final output, filling missing dates with zeros
  return allDates.map((label) => ({
    label,
    data: byDate.has(label)
      ? byDate.get(label)!
      : Object.fromEntries(config.map((c) => [c.name, 0]))
  }));
}

interface PlatformContentMetrics {
  [platform: string]: {
    [postType: string]: Partial<Record<MetricName, number>>;
  };
}
export function getPlatformContentMetrics(
  data: any[],
  platforms: string[],
  config: Array<{ type: string; name: string }>,
  metricConfig: any
): Record<string, Record<string, Partial<Record<MetricName, number>>>> {
  const platformsReversed = [...platforms].reverse();

  const result: Record<
    string,
    Record<string, Partial<Record<MetricName, number>>>
  > = {};

  for (const platform of platformsReversed) {
    if (!isPlatformEnabled[platform]) {
      continue;
    }
    const platformName = PLATFORM_LIST_NAMES[platform] || platform;
    result[platformName] = {};

    for (const { name } of config) {
      result[platformName][name] = {}; // Prepare each postType object
      // Initialize all metric keys to zero
      for (const { metricName } of metricConfig) {
        result[platformName][name]![metricName] = 0;
      }
    }
  }

  // Populate based on actual data entries
  for (const item of data) {
    const overview = item.analytics?.data_as_json?.overview;
    if (!overview) continue;

    for (const platform of platformsReversed) {
      const platformName = PLATFORM_LIST_NAMES[platform] || platform;
      const metricsForPlatform = overview[platform];
      if (!metricsForPlatform) continue;

      const cfg = config.find(
        (c) => item[`content_type_is_${c.type}`] === true
      );
      if (!cfg) continue;

      // Initialize structures if needed
      if (!result[platformName]) {
        result[platformName] = {};
        // Initialize all post types for visible platform
        for (const { name } of config) {
          result[platformName][name] = {};
          for (const { metricName } of metricConfig) {
            result[platformName][name]![metricName] = 0;
          }
        }
      }

      // Accumulate values only for this item’s post type
      for (const { metricName } of metricConfig) {
        const val =
          metricName === METRIC_NAMES.POST_COUNT
            ? 1
            : metricsForPlatform[metricName];
        if (typeof val === 'number') {
          result[platformName][cfg.name]![metricName]! += val;
        }
      }
    }
  }

  // Remove platforms that never had any items
  for (const platformName of Object.keys(result)) {
    const posts = result[platformName];
    const seenAny = Object.values(posts).some((metrics) =>
      Object.values(metrics).some((v) => typeof v === 'number' && v > 0)
    );
    if (
      !seenAny &&
      !isPlatformEnabled[getKeyByValue(PLATFORM_LIST_NAMES, platformName)]
    ) {
      delete result[platformName];
    }
  }

  return result;
}

export function getKeyByValue<T extends Record<string, any>>(
  obj: T,
  value: T[keyof T]
): string | undefined {
  return Object.keys(obj).find((key) => obj[key] === value);
}

export function filterPostsByPlatform(posts, platform) {
  // Platform mapping for post properties
  const platformMapping = {
    [FACEBOOK]: 'facebook_post',
    [INSTAGRAM]: 'instagram_post',
    [GOOGLE]: 'google_post',
    [TWITTER]: 'twitter_post',
    [LINKEDIN_PERSONAL_ACCOUNT]: 'linkedin_personal_account_post',
    [LINKEDIN_BUSINESS_PAGE]: 'linkedin_business_page_post',
    [YOUTUBE]: 'youtube_post',
    [TIKTOK_PERSONAL_ACCOUNT]: 'tiktok_personal_account_post',
    [TIKTOK_BUSINESS_PAGE]: 'tiktok_business_page_post',
    [WHATSAPP]: 'whatsapp_post',
    [PINTEREST]: 'pinterest_post',
    generic: 'generic_post'
  };

  const platformProperty = platformMapping[platform];

  if (!platformProperty) {
    throw new Error(
      `Invalid platform: ${platform}. Valid platforms are: ${Object.keys(
        platformMapping
      ).join(', ')}`
    );
  }

  return posts
    .filter((post) => post[platformProperty] === true)
    .map((post) => {
      // Create a deep copy of the post to avoid mutating the original
      const filteredPost = JSON.parse(JSON.stringify(post));

      // Set all platform flags to false/null except the target platform
      Object.values(platformMapping).forEach((prop) => {
        if (prop === platformProperty) {
          filteredPost[prop] = true;
        } else {
          filteredPost[prop] = filteredPost[prop] === true ? false : null;
        }
      });

      // Modify analytics data if it exists
      if (filteredPost.analytics && filteredPost.analytics.data_as_json) {
        const analytics = filteredPost.analytics.data_as_json;

        // Filter published data for the target platform
        if (analytics.published) {
          analytics.published = analytics.published.filter(
            (p) => p.platform === platform
          );
        }

        // Update overview to only include the target platform
        if (analytics.overview && analytics.overview[platform]) {
          const platformOverview = analytics.overview[platform];
          analytics.overview = {
            [platform]: platformOverview
          };

          // Update summary with platform-specific data
          analytics.summary = {
            impressions: platformOverview.impressions || 0,
            reach: platformOverview.reach || 0,
            shares: platformOverview.shares || 0,
            comments: platformOverview.comments || 0,
            reactions: platformOverview.reactions || 0,
            engagement: platformOverview.engagement || 0,
            share_rate: platformOverview.share_rate || 0,
            comment_to_reaction_ratio:
              platformOverview.comment_to_reaction_ratio || 0,
            interactions: platformOverview.interactions || 0
          };

          // Update the sum_ properties in analytics
          filteredPost.analytics.sum_impressions =
            platformOverview.impressions || 0;
          filteredPost.analytics.sum_reach = platformOverview.reach || 0;
          filteredPost.analytics.sum_reactions =
            platformOverview.reactions || 0;
          filteredPost.analytics.sum_comments = platformOverview.comments || 0;
          filteredPost.analytics.sum_shares = platformOverview.shares || 0;
          filteredPost.analytics.sum_engagement =
            platformOverview.engagement || 0;
          filteredPost.analytics.sum_interactions =
            platformOverview.interactions || 0;
        } else {
          // If platform doesn't exist in overview, set everything to 0/empty
          analytics.overview = {};
          analytics.summary = {
            impressions: 0,
            reach: 0,
            shares: 0,
            comments: 0,
            reactions: 0,
            engagement: 0,
            share_rate: 0,
            comment_to_reaction_ratio: 0,
            interactions: 0
          };

          filteredPost.analytics.sum_impressions = 0;
          filteredPost.analytics.sum_reach = 0;
          filteredPost.analytics.sum_reactions = 0;
          filteredPost.analytics.sum_comments = 0;
          filteredPost.analytics.sum_shares = 0;
          filteredPost.analytics.sum_engagement = 0;
          filteredPost.analytics.sum_interactions = 0;
        }
      }

      return filteredPost;
    });
}
